import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase/client';
import RescheduleForm from '../TherapistCalendar/RescheduleForm';
import WhatsappConfirmButton from '../TherapistCalendar/WhatsappConfirmButton';
import { useConsultationActions } from '../../hooks/useConsultationActions';
import { useConfirm } from '../ui/ConfirmProvider';
import { useToast } from '../ui/ToastProvider';
import ModalShell from '../ui/ModalShell';
import { EmailService } from '../../services/email.service';
import { toSantiagoDateStr, toSantiagoTime } from '../../lib/utils/dateUtils';
import './ClinicCalendar.css';
import '../TherapistCalendar/TherapistCalendar.css';

// ============================================================================
// PALETA DE COLORES — 8 colores WCAG AA (≥4.5:1 sobre #FFF)
// ============================================================================
const THERAPIST_COLORS: string[] = [
  '#3D52A0',
  '#7C3085',
  '#0D7377',
  '#8B1A2E',
  '#5C6B00',
  '#8B4513',
  '#2C4A6E',
  '#1A5235',
];

// ── Sistema temporal ───────────────────────────────────────────────────────
// 60 px/hora (1 px/min). Antes 120 px/hora = 1472 px de grilla para 11 h:
// la semana obligaba a scrollear la página entera para ver una jornada.
// Con 60 px una sesión de 50 min mide 50 px (nombre + hora en 2 líneas) y la
// jornada completa entra en ~720 px dentro de un scroll interno.
const ROW_H        = 60;
const PPM          = ROW_H / 60;
const START_HOUR   = 9;
const END_HOUR     = 20;
const HOURS        = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i);
const GRID_PADDING = 8;
const GRID_HEIGHT  = (END_HOUR - START_HOUR + 1) * ROW_H + GRID_PADDING;
// Bajo esta altura la cita se pinta en una sola línea (hora + nombre)
const TIGHT_EVENT_H = 38;

// ============================================================================
// TIPOS
// ============================================================================
interface TherapistInfo {
  id: string;
  nombre: string;
  apellido: string;
  orden_display: number;
  color: string;
}

interface ClinicAppointment {
  id: string;
  patient_name: string;
  /** Poblado vía get_pacientes_basico_director (RLS no permite el embed
   * directo pacientes(...) para pacientes de terapeutas que no son el
   * director actual) — usado por el botón de confirmación WhatsApp y por
   * la notificación de reagendamiento. */
  patient_phone?: string;
  patient_email?: string;
  therapy_type: string;
  start_time: string;
  end_time: string;
  date: string;
  status: 'agendada' | 'programada' | 'confirmada' | 'en_progreso' | 'completada' | 'cancelada' | 'no_asistio';
  modality: 'presencial' | 'online' | 'domicilio';
  duration: number;
  therapist_id: string;
  therapist_name: string;
  therapist_email?: string;
  therapist_color: string;
}

interface ClinicCalendarProps {
  currentDirectorId: string;
}

type CalendarView = 'day' | 'week' | 'month';

// ============================================================================
// HELPERS PUROS
// ============================================================================
const toLocalDateStr = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const getTherapyColor = (type: string): string => {
  const t = type?.toLowerCase() ?? '';
  if (t.includes('individual')) return '#1A6FBF';
  if (t.includes('pareja') || t.includes('parejas')) return '#C05E00';
  if (t.includes('famil')) return '#057A55';
  if (t.includes('sexo')) return '#6B21A8';
  return '#1A6FBF';
};

const getStatusIcon = (status: string): string => {
  switch (status) {
    case 'agendada':    return 'fa-calendar-plus';
    case 'programada':  return 'fa-clock';
    case 'confirmada':  return 'fa-check';
    case 'en_progreso': return 'fa-play';
    case 'completada':  return 'fa-check-circle';
    case 'cancelada':   return 'fa-times';
    case 'no_asistio':  return 'fa-user-times';
    default:            return 'fa-question';
  }
};

// Presencial es el caso por defecto → sin ícono en los bloques (ruido visual).
// Antes el badge era 'P' : 'O', así que domicilio se mostraba como "O" (online).
const getModalityMeta = (modality: ClinicAppointment['modality']) => {
  switch (modality) {
    case 'online':    return { icon: 'fa-video', label: 'Online' };
    case 'domicilio': return { icon: 'fa-house', label: 'Domicilio' };
    default:          return { icon: 'fa-building', label: 'Presencial' };
  }
};

const getEventPosition = (startTime: string, durationMin: number) => {
  const [h, m] = startTime.split(':').map(Number);
  const top    = (h * 60 + m - START_HOUR * 60) * PPM;
  const height = Math.max(durationMin * PPM, 22);
  return { top, height };
};

const firstName = (fullName: string) => fullName.split(' ')[0];

// "5 – 11 oct" o "29 sept – 5 oct" cuando la semana cruza de mes
const formatWeekRange = (days: Date[]): string => {
  const first = days[0];
  const last  = days[6];
  const month = (d: Date) => d.toLocaleDateString('es-CL', { month: 'short' }).replace('.', '');
  return first.getMonth() === last.getMonth()
    ? `${first.getDate()} – ${last.getDate()} ${month(last)}`
    : `${first.getDate()} ${month(first)} – ${last.getDate()} ${month(last)}`;
};

// Asigna lane (carril) a cada cita para manejar superposición temporal
const computeLanes = (apts: ClinicAppointment[]): Map<string, { lane: number; totalLanes: number }> => {
  const result = new Map<string, { lane: number; totalLanes: number }>();
  if (apts.length === 0) return result;

  const toMin = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  const overlaps = (a: ClinicAppointment, b: ClinicAppointment) => {
    const aS = toMin(a.start_time), aE = aS + a.duration;
    const bS = toMin(b.start_time), bE = bS + b.duration;
    return aS < bE && bS < aE;
  };

  const sorted = [...apts].sort((a, b) => toMin(a.start_time) - toMin(b.start_time));

  // Agrupar en componentes de overlap conectados
  const groups: ClinicAppointment[][] = [];
  for (const apt of sorted) {
    const indices: number[] = [];
    groups.forEach((g, i) => { if (g.some(x => overlaps(apt, x))) indices.push(i); });
    if (indices.length === 0) {
      groups.push([apt]);
    } else {
      const merged: ClinicAppointment[] = [apt];
      for (const i of indices.slice().reverse()) merged.push(...groups.splice(i, 1)[0]);
      groups.push(merged);
    }
  }

  for (const group of groups) {
    const sortedGroup = [...group].sort((a, b) => toMin(a.start_time) - toMin(b.start_time));
    sortedGroup.forEach((apt, lane) => {
      result.set(apt.id, { lane, totalLanes: group.length });
    });
  }

  return result;
};

const getWeekDays = (date: Date): Date[] => {
  const start = new Date(date);
  const day = start.getDay();
  const diff = start.getDate() - day + (day === 0 ? -6 : 1);
  start.setDate(diff);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

// ============================================================================
// SUBCOMPONENTE — FILTRO DE TERAPEUTAS
// ============================================================================
const TherapistFilter: React.FC<{
  therapists: TherapistInfo[];
  selected: Set<string>;
  onChange: (ids: Set<string>) => void;
}> = ({ therapists, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const toggleTherapist = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onChange(next);
  };

  const clearAll = () => onChange(new Set());

  const triggerLabel = selected.size === 0
    ? 'Todos'
    : `${selected.size} terapeuta${selected.size > 1 ? 's' : ''}`;

  return (
    <div className="therapist-filter" ref={ref}>
      <button
        className={`therapist-filter__trigger ${selected.size > 0 ? 'active' : ''}`}
        onClick={() => setOpen(v => !v)}
      >
        <i className="fas fa-filter"></i>
        {triggerLabel}
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`}></i>
      </button>

      {open && (
        <div className="therapist-filter__dropdown">
          <div className="therapist-filter__option" onClick={clearAll}>
            <div className="therapist-filter__color-dot" style={{ background: 'transparent', border: '1px solid var(--apple-separator)' }} />
            <span className="therapist-filter__label">Todos los terapeutas</span>
            {selected.size === 0 && <i className="fas fa-check therapist-filter__check"></i>}
          </div>
          <div className="therapist-filter__separator" />
          {therapists.map(t => (
            <div
              key={t.id}
              className="therapist-filter__option"
              onClick={() => toggleTherapist(t.id)}
            >
              <div className="therapist-filter__color-dot" style={{ background: t.color }} />
              <span className="therapist-filter__label">{t.nombre} {t.apellido}</span>
              {selected.has(t.id) && <i className="fas fa-check therapist-filter__check"></i>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
const ClinicCalendar: React.FC<ClinicCalendarProps> = () => {
  const [currentView, setCurrentView] = useState<CalendarView>(
    () => window.innerWidth < 768 ? 'day' : 'week'
  );
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState<ClinicAppointment[]>([]);
  const [therapistList, setTherapistList] = useState<TherapistInfo[]>([]);
  const [selectedTherapistIds, setSelectedTherapistIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState<ClinicAppointment | null>(null);
  const [isMobileView, setIsMobileView] = useState(() => window.innerWidth < 768);
  const [monthDaySheetDate, setMonthDaySheetDate] = useState<string | null>(null);
  // Reloj para la línea "ahora" — refresca cada minuto
  const [now, setNow] = useState(() => new Date());
  // Contenedor con scroll vertical de la vista activa (semana o día)
  const gridScrollRef = useRef<HTMLDivElement>(null);

  const prevWidthRef = useRef(window.innerWidth);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Evita que una respuesta vieja (día anterior, cambiado rápido) pise el
  // resultado de la última petición — mismo fix que TherapistCalendar.tsx.
  const requestIdRef = useRef(0);

  // Ajustar vista en resize (iOS Safari safe)
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      if (newWidth === prevWidthRef.current) return;
      prevWidthRef.current = newWidth;
      // Semana ya no se fuerza a Día en mobile: tiene su propia vista
      // (chips de días + día expandido, renderMobileWeekView)
      setIsMobileView(newWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadAppointments = async (knownTherapists: TherapistInfo[]) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const startRange = new Date(selectedDate.getTime() - 60 * 24 * 60 * 60 * 1000);
      const endRange   = new Date(selectedDate.getTime() + 90 * 24 * 60 * 60 * 1000);

      const { data: consultas, error } = await supabase
        .from('consultas')
        .select(`
          id, fecha_hora, duracion, modalidad, estado, tipo_consulta,
          valor_consulta, notas_terapeuta, terapeuta_id, paciente_id,
          pacientes(nombre, apellido),
          terapeutas(id, nombre, apellido, orden_display, email)
        `)
        .gte('fecha_hora', startRange.toISOString())
        .lte('fecha_hora', endRange.toISOString())
        .order('fecha_hora', { ascending: true });

      if (error) throw error;

      // El embed pacientes(nombre, apellido) queda bloqueado por RLS para
      // consultas de terapeutas que no son el director actual (RLS de
      // `pacientes` no da acceso de fila completa a director sobre pacientes
      // ajenos — ver SECCIÓN E de
      // sql/migrations/therapist_director_calendar_actions_2026_07_28.sql).
      // Se resuelve nombre/apellido/teléfono confiables con una RPC
      // SECURITY DEFINER de columnas acotadas (nunca RUT ni datos de salud).
      const pacienteIds = Array.from(
        new Set((consultas || []).map((c: any) => c.paciente_id).filter(Boolean))
      ) as string[];

      const pacientesBasico = new Map<string, { nombre: string; apellido: string; telefono: string | null; email: string | null }>();
      if (pacienteIds.length > 0) {
        const { data: pacientesData, error: pacientesError } = await supabase
          .rpc('get_pacientes_basico_director', { p_paciente_ids: pacienteIds });

        if (pacientesError) {
          console.error('Error al cargar datos básicos de pacientes (director):', pacientesError.message);
        } else if (pacientesData) {
          (pacientesData as { id: string; nombre: string; apellido: string; telefono: string | null; email: string | null }[]).forEach(p => {
            pacientesBasico.set(p.id, { nombre: p.nombre, apellido: p.apellido, telefono: p.telefono, email: p.email });
          });
        }
      }

      const transformed: ClinicAppointment[] = (consultas || []).map((c: any) => {
        const fechaHora = new Date(c.fecha_hora);
        const endTime   = new Date(fechaHora.getTime() + (c.duracion || 50) * 60000);

        let patientName = 'Paciente';
        if (c.pacientes) {
          if (Array.isArray(c.pacientes) && c.pacientes.length > 0) {
            patientName = `${c.pacientes[0].nombre ?? ''} ${c.pacientes[0].apellido ?? ''}`.trim();
          } else if (c.pacientes.nombre) {
            patientName = `${c.pacientes.nombre} ${c.pacientes.apellido ?? ''}`.trim();
          }
        }

        // La RPC es la fuente confiable (bypasea RLS) — pisa el nombre del
        // embed cuando hay dato disponible, y es la única fuente de
        // teléfono/email.
        let patientPhone: string | undefined;
        let patientEmail: string | undefined;
        const basico = c.paciente_id ? pacientesBasico.get(c.paciente_id) : undefined;
        if (basico) {
          patientName  = `${basico.nombre ?? ''} ${basico.apellido ?? ''}`.trim() || patientName;
          patientPhone = basico.telefono ?? undefined;
          patientEmail = basico.email ?? undefined;
        }

        const therapistRaw   = Array.isArray(c.terapeutas) ? c.terapeutas[0] : c.terapeutas;
        const therapistId    = therapistRaw?.id ?? c.terapeuta_id ?? '';
        const therapistColor = knownTherapists.find(t => t.id === therapistId)?.color ?? THERAPIST_COLORS[0];
        const therapistName  = therapistRaw
          ? `${therapistRaw.nombre ?? ''} ${therapistRaw.apellido ?? ''}`.trim()
          : 'Terapeuta';
        const therapistEmail = therapistRaw?.email ?? undefined;

        return {
          id: c.id,
          patient_name: patientName || 'Paciente',
          patient_phone: patientPhone,
          patient_email: patientEmail,
          therapy_type: c.tipo_consulta || 'individual',
          start_time: fechaHora.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false }),
          end_time: endTime.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false }),
          date: toLocalDateStr(fechaHora),
          status: (c.estado as ClinicAppointment['status']) || 'programada',
          modality: c.modalidad || 'presencial',
          duration: c.duracion || 50,
          therapist_id: therapistId,
          therapist_name: therapistName,
          therapist_email: therapistEmail,
          therapist_color: therapistColor,
        };
      });

      if (requestId !== requestIdRef.current) return; // llegó una petición más nueva primero, se descarta esta
      setAppointments(transformed);
    } catch (err) {
      if (requestId === requestIdRef.current) console.error('Error al cargar agenda clínica:', err);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  // Carga inicial: terapeutas primero, citas después con la lista ya resuelta
  useEffect(() => {
    const init = async () => {
      const { data } = await supabase
        .from('terapeutas')
        .select('id, nombre, apellido, orden_display')
        .eq('activo', true)
        .order('orden_display', { ascending: true });

      const withColors: TherapistInfo[] = (data ?? []).map((t, idx) => ({
        id: t.id,
        nombre: t.nombre,
        apellido: t.apellido,
        orden_display: t.orden_display ?? idx,
        color: THERAPIST_COLORS[idx % THERAPIST_COLORS.length],
      }));

      setTherapistList(withColors);
      await loadAppointments(withColors);
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recarga al cambiar fecha (terapeutas ya están en estado)
  useEffect(() => {
    if (therapistList.length > 0) {
      loadAppointments(therapistList);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Aplicar filtro en memoria
  const filteredAppointments: ClinicAppointment[] =
    selectedTherapistIds.size === 0
      ? appointments
      : appointments.filter(a => selectedTherapistIds.has(a.therapist_id));

  const getAppointmentsForDate = (dateStr: string): ClinicAppointment[] =>
    filteredAppointments.filter(a => a.date === dateStr && a.status !== 'cancelada');

  // ============================================================================
  // NAVEGACIÓN
  // ============================================================================
  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    switch (currentView) {
      case 'day':   newDate.setDate(newDate.getDate()   + (direction === 'next' ? 1  : -1));  break;
      case 'week':  newDate.setDate(newDate.getDate()   + (direction === 'next' ? 7  : -7));  break;
      case 'month': newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1  : -1));  break;
    }
    setSelectedDate(newDate);
  };

  // Al abrir Día/Semana, el grid arranca donde importa: 1 h antes de "ahora"
  // si el período incluye hoy; si no, 30 min antes de la primera cita.
  useEffect(() => {
    const el = gridScrollRef.current;
    if (!el || currentView === 'month') return;

    const visibleDates = currentView === 'week' && !isMobileView
      ? getWeekDays(selectedDate).map(toLocalDateStr)
      : [toLocalDateStr(selectedDate)];

    let targetMin: number | null = null;
    if (visibleDates.includes(toLocalDateStr(new Date()))) {
      const n = new Date();
      targetMin = n.getHours() * 60 + n.getMinutes() - 60;
    } else {
      const starts = visibleDates
        .flatMap(d => getAppointmentsForDate(d))
        .map(a => { const [h, m] = a.start_time.split(':').map(Number); return h * 60 + m; });
      if (starts.length > 0) targetMin = Math.min(...starts) - 30;
    }

    el.scrollTop = targetMin === null ? 0 : Math.max(0, (targetMin - START_HOUR * 60) * PPM);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, selectedDate, loading, isMobileView]);

  // ============================================================================
  // RENDERS DE VISTAS
  // ============================================================================
  const renderHourLines = () => (
    <>
      {HOURS.map(h => (
        <div key={h} className="cc-hline cc-hline--hour" style={{ top: (h - START_HOUR) * ROW_H }} />
      ))}
      {HOURS.slice(0, -1).map(h => (
        <div key={`hh${h}`} className="cc-hline cc-hline--half" style={{ top: (h - START_HOUR) * ROW_H + ROW_H / 2 }} />
      ))}
    </>
  );

  const renderTimeGutter = () => (
    <div className="cc-gutter cc-gutter-sticky" style={{ height: GRID_HEIGHT }}>
      {HOURS.map(h => (
        <div
          key={h}
          className="cc-time-label"
          style={{ top: (h - START_HOUR) * ROW_H, ...(h === START_HOUR ? { transform: 'none', paddingTop: 2 } : {}) }}
        >
          {String(h).padStart(2, '0')}:00
        </div>
      ))}
    </div>
  );

  // Línea roja de hora actual — solo en la columna de hoy y dentro del rango
  const renderNowLine = (dateStr: string) => {
    if (dateStr !== toLocalDateStr(now)) return null;
    const minutes = now.getHours() * 60 + now.getMinutes();
    if (minutes < START_HOUR * 60 || minutes > (END_HOUR + 1) * 60) return null;
    return (
      <div className="cc-now-line" style={{ top: (minutes - START_HOUR * 60) * PPM }} aria-hidden="true" />
    );
  };

  // Bloque de cita — fondo tintado + barra de acento del color de terapia.
  // `showTherapist`: semana (varias agendas mezcladas) pinta el punto del
  // terapeuta; en Día la columna ya lo identifica.
  const renderEvent = (
    apt: ClinicAppointment,
    lanes: Map<string, { lane: number; totalLanes: number }>,
    opts: { showTherapist: boolean; shortName: boolean },
  ) => {
    const { top, height } = getEventPosition(apt.start_time, apt.duration);
    const { lane, totalLanes } = lanes.get(apt.id) ?? { lane: 0, totalLanes: 1 };
    const widthPct = 100 / totalLanes;
    const leftPct  = (lane / totalLanes) * 100;
    const modality = getModalityMeta(apt.modality);
    const name     = opts.shortName ? firstName(apt.patient_name) : apt.patient_name;
    const tight    = height < TIGHT_EVENT_H;

    return (
      <button
        type="button"
        key={apt.id}
        className={`cc-event ${apt.status}${tight ? ' cc-event--tight' : ''}`}
        style={{
          top,
          height,
          left:  `calc(${leftPct}% + 2px)`,
          width: `calc(${widthPct}% - 4px)`,
          '--evt-color': getTherapyColor(apt.therapy_type),
        } as React.CSSProperties}
        title={`${apt.start_time}–${apt.end_time} · ${apt.patient_name} · ${apt.therapist_name} · ${modality.label}`}
        onClick={e => { e.stopPropagation(); setSelectedAppointment(apt); }}
      >
        <span className="cc-evt-name">
          {opts.showTherapist && (
            <span className="cc-evt-therapist-dot" style={{ background: apt.therapist_color }} />
          )}
          {tight && <span className="cc-evt-time">{apt.start_time}</span>}
          <span className="cc-evt-name-text">{name}</span>
        </span>
        {!tight && (
          <span className="cc-evt-meta">
            <span className="cc-evt-time">{apt.start_time}–{apt.end_time}</span>
            {apt.modality !== 'presencial' && (
              <i className={`fas ${modality.icon}`} aria-label={modality.label} />
            )}
            {apt.status === 'confirmada' && <i className="fas fa-check" aria-label="Confirmada" />}
          </span>
        )}
      </button>
    );
  };
  // ============================================================================
  // Grid temporal de la vista Día — reutilizado por vista Día desktop y por
  // la tira de chips + día expandido de vista Semana mobile.
  const renderDayGrid = (dateStr: string) => {
    const dayApts = getAppointmentsForDate(dateStr);

    return (
      <div className="cc-scroll-wrap" ref={gridScrollRef}>
        {/* Cabecera de columnas de terapeuta — hijo de cc-scroll-wrap para
            compartir el mismo eje de scroll horizontal que las columnas */}
        <div className="cc-col-header" style={{ gridTemplateColumns: `var(--cc-gutter-w) repeat(${visibleTherapists.length}, minmax(120px, 1fr))` }}>
          <div className="cc-gutter-spacer" />
          {visibleTherapists.map(t => (
            <div key={t.id} className="cc-therapist-hdr">
              <span className="cc-therapist-dot" style={{ background: t.color }} />
              <span className="cc-therapist-hdr-name">{t.nombre}</span>
            </div>
          ))}
        </div>

        <div className="cc-time-grid" style={{ gridTemplateColumns: `var(--cc-gutter-w) repeat(${visibleTherapists.length}, minmax(120px, 1fr))` }}>
          {renderTimeGutter()}

          {/* Columnas por terapeuta */}
          {visibleTherapists.map(t => {
            const tApts = dayApts.filter(a => a.therapist_id === t.id);
            const lanes = computeLanes(tApts);

            return (
              <div key={t.id} className="cc-therapist-col" style={{ height: GRID_HEIGHT }}>
                {renderHourLines()}
                {renderNowLine(dateStr)}
                {tApts.map(apt => renderEvent(apt, lanes, { showTherapist: false, shortName: false }))}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const dateStr = toLocalDateStr(selectedDate);

    return (
      <div className="cc-day-view">
        {renderDayGrid(dateStr)}
      </div>
    );
  };

  // Vista semanal mobile — chips de días (con contador) + día expandido
  // (columnas por terapeuta). La grilla de 7 columnas no entra en 390 px.
  const renderMobileWeekView = () => {
    const weekDays      = getWeekDays(selectedDate);
    const todayStr      = toLocalDateStr(now);
    const activeDateStr = toLocalDateStr(selectedDate);
    const totalCitas    = getAppointmentsForDate(activeDateStr).length;

    return (
      <div className="cc-day-view cc-mobile-week">
        <div className="cc-week-chips" role="tablist" aria-label="Días de la semana">
          {weekDays.map((day, i) => {
            const dStr     = toLocalDateStr(day);
            const isActive = dStr === activeDateStr;
            const isToday  = dStr === todayStr;
            const count    = getAppointmentsForDate(dStr).length;
            return (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`cc-week-chip${isActive ? ' active' : ''}${isToday ? ' today' : ''}`}
                onClick={() => setSelectedDate(day)}
              >
                <span className="cc-chip-day-name">
                  {day.toLocaleDateString('es-CL', { weekday: 'narrow' })}
                </span>
                <span className="cc-chip-day-num">{day.getDate()}</span>
                <span className="cc-chip-count" aria-label={`${count} citas`}>{count > 0 ? count : ''}</span>
              </button>
            );
          })}
        </div>

        <div className="cc-day-title-bar">
          <h3 className="cc-day-title">
            {selectedDate.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' })}
          </h3>
          <span className="cc-day-stats">{totalCitas} cita{totalCitas !== 1 ? 's' : ''}</span>
        </div>

        {renderDayGrid(activeDateStr)}
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDays = getWeekDays(selectedDate);
    const todayStr = toLocalDateStr(now);

    return (
      // Un solo contenedor de scroll (vertical + horizontal): header de días
      // sticky arriba, gutter de horas sticky a la izquierda.
      <div className="cc-week-view" ref={gridScrollRef}>
        <div className="cc-week-inner">
          <div className="cc-week-header">
            <div className="cc-gutter-spacer" />
            {weekDays.map((day, i) => {
              const dStr    = toLocalDateStr(day);
              const isToday = dStr === todayStr;
              const count   = getAppointmentsForDate(dStr).length;
              return (
                <button
                  key={i}
                  type="button"
                  className={`cc-week-day-hdr${isToday ? ' today' : ''}`}
                  onClick={() => { setSelectedDate(day); setCurrentView('day'); }}
                  title="Ver día por terapeuta"
                >
                  <span className="cc-week-day-name">
                    {day.toLocaleDateString('es-CL', { weekday: 'short' }).replace('.', '')}
                  </span>
                  <span className="cc-week-day-num">{day.getDate()}</span>
                  {count > 0 && <span className="cc-week-day-count">{count}</span>}
                </button>
              );
            })}
          </div>

          <div className="cc-time-grid cc-week-grid">
            {renderTimeGutter()}

            {weekDays.map((day, dayIndex) => {
              const dateStr = toLocalDateStr(day);
              const isToday = dateStr === todayStr;
              const dayApts = getAppointmentsForDate(dateStr);
              const lanes   = computeLanes(dayApts);

              return (
                <div key={dayIndex} className={`cc-day-col${isToday ? ' today' : ''}`} style={{ height: GRID_HEIGHT }}>
                  {renderHourLines()}
                  {renderNowLine(dateStr)}
                  {dayApts.map(apt => renderEvent(apt, lanes, { showTherapist: true, shortName: true }))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const firstDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const lastDay  = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    const daysInMonth: { date: Date; isCurrentMonth: boolean }[] = [];

    const firstWeekday = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    for (let i = firstWeekday - 1; i >= 0; i--) {
      const d = new Date(firstDay);
      d.setDate(d.getDate() - i - 1);
      daysInMonth.push({ date: d, isCurrentMonth: false });
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      daysInMonth.push({ date: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), d), isCurrentMonth: true });
    }
    // 5 filas cuando el mes cabe (antes siempre 6 → una semana extra del mes siguiente)
    const remaining = Math.ceil(daysInMonth.length / 7) * 7 - daysInMonth.length;
    for (let d = 1; d <= remaining; d++) {
      daysInMonth.push({ date: new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, d), isCurrentMonth: false });
    }

    return (
      <div className="calendar-month-view cc-month-view">
        <div className="month-header">
          <div className="weekday-labels">
            {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => (
              <div key={d} className="weekday-label">{d}</div>
            ))}
          </div>
        </div>

        <div className="month-grid">
          {daysInMonth.map((dayInfo, idx) => {
            const dateStr  = toLocalDateStr(dayInfo.date);
            const dayApts  = getAppointmentsForDate(dateStr);
            const isToday  = dateStr === toLocalDateStr(now);

            return (
              <div
                key={idx}
                className={`month-day${!dayInfo.isCurrentMonth ? ' other-month' : ''}${isToday ? ' today' : ''}${isMobileView ? ' month-day--mobile' : ''}`}
                onClick={() => {
                  if (isMobileView) {
                    setMonthDaySheetDate(dateStr);
                  } else {
                    setSelectedDate(dayInfo.date);
                    setCurrentView('day');
                  }
                }}
              >
                <div className="day-number">{dayInfo.date.getDate()}</div>
                {isMobileView ? (
                  <div className="day-dots">
                    {dayApts.slice(0, 4).map(apt => (
                      <span
                        key={apt.id}
                        className="month-dot"
                        style={{ backgroundColor: getTherapyColor(apt.therapy_type) }}
                      />
                    ))}
                    {dayApts.length > 4 && (
                      <span className="month-dot-overflow">+{dayApts.length - 4}</span>
                    )}
                  </div>
                ) : (
                  <div className="day-appointments">
                    {dayApts.slice(0, 3).map(apt => (
                      <button
                        type="button"
                        key={apt.id}
                        className={`appointment-indicator cc-month-chip ${apt.status}`}
                        style={{ '--evt-color': getTherapyColor(apt.therapy_type) } as React.CSSProperties}
                        title={`${apt.start_time} · ${apt.patient_name} · ${apt.therapist_name} · ${getModalityMeta(apt.modality).label}`}
                        onClick={e => { e.stopPropagation(); setSelectedAppointment(apt); }}
                      >
                        <span className="cc-evt-therapist-dot" style={{ background: apt.therapist_color }} />
                        <span className="apt-indicator-time">{apt.start_time}</span>
                        <span className="apt-indicator-name">{firstName(apt.patient_name)}</span>
                      </button>
                    ))}
                    {dayApts.length > 3 && (
                      <div className="more-appointments">+{dayApts.length - 3} más</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ============================================================================
  // RENDER PRINCIPAL
  // ============================================================================
  if (loading) {
    return (
      <div className="therapist-calendar clinic-calendar loading">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Cargando agenda clínica...</p>
        </div>
      </div>
    );
  }

  const visibleTherapists = selectedTherapistIds.size === 0
    ? therapistList
    : therapistList.filter(t => selectedTherapistIds.has(t.id));

  // Citas del período visible (respeta filtro y excluye canceladas)
  const periodCount = (() => {
    if (currentView === 'day') return getAppointmentsForDate(toLocalDateStr(selectedDate)).length;
    if (currentView === 'week') {
      return getWeekDays(selectedDate).reduce((n, d) => n + getAppointmentsForDate(toLocalDateStr(d)).length, 0);
    }
    const prefix = toLocalDateStr(selectedDate).slice(0, 7);
    return filteredAppointments.filter(a => a.date.startsWith(prefix) && a.status !== 'cancelada').length;
  })();

  return (
    <div className="therapist-calendar clinic-calendar">
      {/* Header */}
      <div className="calendar-header">
        <div className="calendar-navigation">
          <button className="nav-btn" onClick={() => navigateDate('prev')} aria-label="Período anterior">
            <i className="fas fa-chevron-left"></i>
          </button>
          <div className="current-period">
            <h2>
              {currentView === 'month' && selectedDate.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })}
              {currentView === 'week' && formatWeekRange(getWeekDays(selectedDate))}
              {currentView === 'day' && (
                <>
                  <span className="period-long">{selectedDate.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                  <span className="period-short">{selectedDate.toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                </>
              )}
            </h2>
            <span className="cc-period-count">{periodCount} cita{periodCount !== 1 ? 's' : ''}</span>
          </div>
          <button className="nav-btn" onClick={() => navigateDate('next')} aria-label="Período siguiente">
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        <div className="calendar-controls">
          <div className="view-switcher">
            {(['day', 'week', 'month'] as CalendarView[]).map(view => (
              <button
                key={view}
                className={`view-btn ${currentView === view ? 'active' : ''}`}
                onClick={() => setCurrentView(view)}
              >
                {view === 'day' ? 'Día' : view === 'week' ? 'Semana' : 'Mes'}
              </button>
            ))}
          </div>

          <TherapistFilter
            therapists={therapistList}
            selected={selectedTherapistIds}
            onChange={setSelectedTherapistIds}
          />

          <button className="today-btn" onClick={() => setSelectedDate(new Date())}>
            Hoy
          </button>
        </div>
      </div>

      {/* Leyenda — tipos de terapia (paridad con TherapistCalendar) + íconos
          de modalidad (presencial es el default y no lleva ícono) */}
      <div className="calendar-legend">
        {[
          { color: '#1A6FBF', label: 'Individual' },
          { color: '#C05E00', label: 'Parejas'    },
          { color: '#057A55', label: 'Familia'    },
          { color: '#6B21A8', label: 'Sexología'  },
        ].map(({ color, label }) => (
          <div key={label} className="legend-item">
            <div className="legend-color" style={{ backgroundColor: color }}></div>
            <span>{label}</span>
          </div>
        ))}
        <span className="cc-legend-sep" aria-hidden="true" />
        <div className="legend-item"><i className="fas fa-video" aria-hidden="true" /><span>Online</span></div>
        <div className="legend-item"><i className="fas fa-house" aria-hidden="true" /><span>Domicilio</span></div>
        <div className="legend-item"><i className="fas fa-check" aria-hidden="true" /><span>Confirmada</span></div>
      </div>

      {/* Contenido del calendario */}
      <div className="calendar-content">
        {currentView === 'day'   && renderDayView()}
        {currentView === 'week'  && (isMobileView ? renderMobileWeekView() : renderWeekView())}
        {currentView === 'month' && renderMonthView()}
      </div>

      {/* Modal de detalle — el director puede confirmar/cancelar/reagendar
          cualquier consulta de la clínica (autorización server-side vía
          rol='director' en las RPCs, ver useConsultationActions). */}
      {selectedAppointment && (
        <ClinicAppointmentModal
          appointment={selectedAppointment}
          onClose={() => setSelectedAppointment(null)}
          onUpdate={() => {
            setSelectedAppointment(null);
            loadAppointments(therapistList);
          }}
          onStatusChange={(newStatus) => {
            const id = selectedAppointment.id;
            setAppointments(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a));
            setSelectedAppointment(prev => (prev && prev.id === id) ? { ...prev, status: newStatus } : prev);
          }}
        />
      )}

      {monthDaySheetDate && (
        <ClinicMonthDaySheet
          dateStr={monthDaySheetDate}
          appointments={getAppointmentsForDate(monthDaySheetDate)}
          onClose={() => setMonthDaySheetDate(null)}
          onSelectAppointment={(apt) => { setMonthDaySheetDate(null); setSelectedAppointment(apt); }}
        />
      )}
    </div>
  );
};

// Bottom sheet — detalle del día desde vista mensual mobile (agenda clínica,
// múltiples terapeutas — muestra a quién pertenece cada cita en vez de método de pago)
const ClinicMonthDaySheet: React.FC<{
  dateStr: string;
  appointments: ClinicAppointment[];
  onClose: () => void;
  onSelectAppointment: (apt: ClinicAppointment) => void;
}> = ({ dateStr, appointments, onClose, onSelectAppointment }) => {
  const date = new Date(dateStr + 'T12:00:00');
  const label = date.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="day-sheet-overlay" onClick={onClose}>
      <div className="day-sheet" onClick={e => e.stopPropagation()}>
        <div className="day-sheet-handle" />
        <div className="day-sheet-header">
          <h3 className="day-sheet-title">{label}</h3>
          <button className="day-sheet-close" onClick={onClose}>
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="day-sheet-list">
          {appointments.length === 0 ? (
            <p className="day-sheet-empty">Sin citas este día</p>
          ) : (
            appointments.map(apt => (
              <div
                key={apt.id}
                className="day-sheet-item"
                onClick={() => onSelectAppointment(apt)}
              >
                <span className="day-sheet-dot" style={{ backgroundColor: getTherapyColor(apt.therapy_type) }} />
                <span className="day-sheet-time">{apt.start_time}</span>
                <div className="day-sheet-patient">
                  <div className="day-sheet-patient-name">{apt.patient_name}</div>
                  <div className="day-sheet-patient-type">{apt.therapy_type}</div>
                </div>
                <span className="day-sheet-modality" title={getModalityMeta(apt.modality).label}>
                  <i className={`fas ${getModalityMeta(apt.modality).icon}`} aria-label={getModalityMeta(apt.modality).label} />
                </span>
                <span
                  className="day-sheet-dot"
                  style={{ backgroundColor: apt.therapist_color }}
                  title={apt.therapist_name}
                  aria-label={apt.therapist_name}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MODAL DE DETALLE — el director puede confirmar/cancelar/reagendar
// CUALQUIER consulta de la clínica (autorización dueño-o-director resuelta
// 100% server-side en las RPCs — no hace falta lógica de permisos acá).
// ============================================================================
const ClinicAppointmentModal: React.FC<{
  appointment: ClinicAppointment;
  onClose: () => void;
  onUpdate: () => void;
  onStatusChange: (newStatus: ClinicAppointment['status']) => void;
}> = ({ appointment, onClose, onUpdate, onStatusChange }) => {
  const therapyColor = getTherapyColor(appointment.therapy_type);
  const [showReschedule, setShowReschedule] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();
  const {
    actionLoading,
    actionError,
    clearActionError,
    advanceStatus,
    cancelConsultation,
    rescheduleConsultation,
  } = useConsultationActions();

  const canChangeStatus = appointment.status !== 'completada' && appointment.status !== 'cancelada';
  const canCancel       = appointment.status !== 'completada' && appointment.status !== 'cancelada';

  const statusLabels: Record<string, string> = {
    programada: 'Confirmar cita',
    confirmada: 'Marcar completada',
  };

  const handleChangeStatus = async () => {
    if (!canChangeStatus) return;
    const newStatus = await advanceStatus(appointment.id);
    if (newStatus) {
      onStatusChange(newStatus as ClinicAppointment['status']);
    } else {
      toast.error('No se pudo actualizar el estado. Intentá nuevamente.');
    }
  };

  const handleCancelAppointment = async () => {
    const confirmed = await confirm({
      title: '¿Cancelar esta cita?',
      message: `Paciente: ${appointment.patient_name}\nFecha: ${appointment.date} · ${appointment.start_time}`,
      confirmLabel: 'Cancelar cita',
      cancelLabel: 'Volver',
      tone: 'danger',
    });
    if (!confirmed) return;

    const ok = await cancelConsultation(appointment.id);
    if (ok) {
      onUpdate();
    } else {
      toast.error('No se pudo cancelar la cita. Intentá nuevamente.');
    }
  };

  const handleReschedule = async (newFechaHoraISO: string) => {
    const oldDate = appointment.date;
    const oldTime = appointment.start_time;
    const ok = await rescheduleConsultation(appointment.id, newFechaHoraISO);
    if (ok) {
      if (appointment.patient_email) {
        const newDT = new Date(newFechaHoraISO);
        await EmailService.sendRescheduleNotification({
          patientName:    appointment.patient_name,
          patientEmail:   appointment.patient_email,
          therapistName:  appointment.therapist_name,
          therapistEmail: appointment.therapist_email ?? 'angeldiazdiaz01@gmail.com',
          oldDate,
          oldTime,
          newDate: toSantiagoDateStr(newDT),
          newTime: toSantiagoTime(newDT),
          modality: appointment.modality,
        });
      }
      setShowReschedule(false);
      onUpdate();
    }
    // Si falló, actionError ya quedó seteado por el hook y se muestra inline en RescheduleForm.
  };

  return (
    <ModalShell title="Detalle de Cita" onClose={onClose}>
      <div className="appointment-info">
            <div className="info-row">
              <label>Terapeuta:</label>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: appointment.therapist_color,
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
                {appointment.therapist_name}
              </span>
            </div>
            <div className="info-row">
              <label>Paciente:</label>
              <span>{appointment.patient_name}</span>
            </div>
            <div className="info-row">
              <label>Tipo de Terapia:</label>
              <span className="therapy-badge" style={{ backgroundColor: therapyColor }}>
                {appointment.therapy_type.charAt(0).toUpperCase() + appointment.therapy_type.slice(1)}
              </span>
            </div>
            <div className="info-row">
              <label>Fecha y Hora:</label>
              <span>{appointment.date} · {appointment.start_time} – {appointment.end_time}</span>
            </div>
            <div className="info-row">
              <label>Duración:</label>
              <span>{appointment.duration} minutos</span>
            </div>
            <div className="info-row">
              <label>Modalidad:</label>
              <span>{appointment.modality === 'presencial' ? 'Presencial' : appointment.modality === 'domicilio' ? 'Domicilio' : 'Online'}</span>
            </div>
            <div className="info-row">
              <label>Estado:</label>
              <span className={`status-badge ${appointment.status}`}>
                <i className={`fas ${getStatusIcon(appointment.status)}`}></i>
                {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
              </span>
            </div>
          </div>

          <WhatsappConfirmButton
            patientFirstName={appointment.patient_name.split(' ')[0]}
            patientPhone={appointment.patient_phone}
            date={appointment.date}
            time={appointment.start_time}
            endTime={appointment.end_time}
            onSent={onClose}
          />

          {showReschedule ? (
            <RescheduleForm
              initialDate={appointment.date}
              initialTime={appointment.start_time}
              therapistId={appointment.therapist_id}
              duration={appointment.duration}
              loading={actionLoading}
              errorMessage={actionError}
              onConfirm={handleReschedule}
              onCancel={() => { setShowReschedule(false); clearActionError(); }}
            />
          ) : (
            <div className="consult-actions">
              {canChangeStatus && (
                <button
                  className="consult-action-btn consult-action-btn--primary"
                  onClick={handleChangeStatus}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <i className="fas fa-spinner fa-spin"></i>
                  ) : (
                    <i className="fas fa-arrow-right"></i>
                  )}
                  {statusLabels[appointment.status] ?? 'Cambiar Estado'}
                </button>
              )}
              {canCancel && (
                <div className="consult-actions__row">
                  <button
                    className="consult-action-btn consult-action-btn--outline"
                    onClick={() => setShowReschedule(true)}
                    disabled={actionLoading}
                  >
                    <i className="fas fa-calendar-alt"></i>
                    Reagendar
                  </button>
                  <button
                    className="consult-action-btn consult-action-btn--danger-soft"
                    onClick={handleCancelAppointment}
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fas fa-times"></i>
                    )}
                    Cancelar cita
                  </button>
                </div>
              )}
              {!canChangeStatus && !canCancel && (
                <p className="modal-status-final">
                  Esta cita ya no puede ser modificada
                </p>
              )}
            </div>
          )}
    </ModalShell>
  );
};

export default ClinicCalendar;
